import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { Permissions } from '@/graphql';
import { useFingerprint } from '@/hooks/useFingerprint';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { Capacitor } from '@capacitor/core';
import { isPlatform } from '@ionic/react';
import axios from 'axios';
import { isNil } from 'ramda';
import { useLocation } from 'react-router';

import { useAuth } from '../AuthProvider';
import { usePermissions } from '../PermissionsProvider';
import { useTeam } from '../TeamProvider';

const platform = Capacitor.getPlatform();

declare let gtag: any;

type SendEventArgs = {
	name?: string;
	title?: string;
	url?: string;
	extraData?: { [key: string]: any };
};

export type TrackPageArgs = { url?: string; title?: string };

interface AnalyticsContext {
	trackPage: (args?: TrackPageArgs) => void;
	trackEvent: (name: string, extraData?: { [key: string]: string }) => void;
}

const AnalyticsContext = createContext<AnalyticsContext>({
	trackPage: () => {},
	trackEvent: () => {},
});

export function useAnalytics() {
	const context = useContext(AnalyticsContext);
	// Note that we don't throw an error if the context is empty here as it is
	// not critical to the app to not have Analytics yes. When the AnalyticsContext.Provider
	// is loaded it will fill the context with the right state (value) and useAnalytics will
	// return the right functions.
	return context;
}

// Copy from <root>/analytics/pages/api/send.ts:11
export interface CollectRequestBody {
	payload: {
		data: { [key: string]: any };
		hostname: string;
		language: string;
		referrer: string;
		screen: string;
		title: string;
		url: string;
		website: string;
		name?: string;
		userId?: string;
		teamId?: string;
		browser?: string;
		os?: string;
	};
	type: string;
}

export const AnalyticsProvider: React.FC = ({ children }) => {
	const { user, isLoading: isUserLoading } = useAuth();
	const { team, isLoading: isTeamLoading } = useTeam();
	const { hasPermission } = usePermissions();
	const {
		analytics: { url: analyticsUrl, websiteId, debug },
		client: { url: clientUrl },
	} = useEnvironment();
	const location = useLocation();
	const fingerprint = useFingerprint();

	// Refs mirror the latest user/team/loading state so a call to sendEvent
	// that has to wait for a transient loading flag can re-check fresh state
	// after waiting, instead of using values captured (and now possibly
	// stale) at call time. Plain closure variables can't do this — they're
	// fixed for the lifetime of a single sendEvent invocation.
	const userRef = useRef(user);
	const teamRef = useRef(team);
	const isUserLoadingRef = useRef(isUserLoading);
	const isTeamLoadingRef = useRef(isTeamLoading);
	useEffect(() => {
		userRef.current = user;
	}, [user]);
	useEffect(() => {
		teamRef.current = team;
	}, [team]);
	useEffect(() => {
		isUserLoadingRef.current = isUserLoading;
	}, [isUserLoading]);
	useEffect(() => {
		isTeamLoadingRef.current = isTeamLoading;
	}, [isTeamLoading]);

	const sendEvent = useCallback(
		async (args?: SendEventArgs) => {
			const { name, title, url, extraData = {} } = args || {};
			const eventType = name ? 'event' : 'pageview';
			try {
				// user/team loading is often a brief, transient race (e.g. a
				// background refetch triggered by a mutation moments earlier) —
				// silently dropping the event here used to lose it permanently
				// with no error and no retry. Wait briefly (bounded) instead,
				// re-checking fresh state via refs, and only give up if it's
				// still loading after the timeout.
				const maxWaitMs = 3000;
				const intervalMs = 150;
				const waitStart = Date.now();
				while (isUserLoadingRef.current || isTeamLoadingRef.current) {
					if (Date.now() - waitStart >= maxWaitMs) {
						if (debug) {
							console.log(
								`[AnalyticsProvider] Gave up waiting for user/team to load, dropping ${eventType}`,
							);
						}
						return;
					}
					await new Promise((resolve) => setTimeout(resolve, intervalMs));
				}

				const postData: CollectRequestBody = {
					payload: {
						hostname: clientUrl,
						language: fingerprint.language,
						referrer: '',
						screen: `${fingerprint.width}x${fingerprint.height}`,
						title: title || document.title || '',
						url: url || location.pathname,
						website: websiteId,
						name, // If name is undefined then the event will be a page view
						data: extraData,
						userId: userRef.current.id,
						teamId: teamRef.current.id,
					},
					type: 'event',
				};
				if (debug) {
					console.log(
						`[AnalyticsProvider] Sending ${eventType}:`,
						postData.payload.name || postData.payload.title,
						postData.payload.data,
						postData,
					);
				}
				if (hasPermission(Permissions.DevtoolsRead)) {
					console.log(
						`[AnalyticsProvider] Skipped ${eventType} as user has the ${Permissions.DevtoolsRead} action`,
					);
					return;
				}
				if (platform !== 'web') {
					// This will make sure a new type of browser is created in umami analytics
					// and we can filter by all "Native" browsers, which will result in data only from
					// native platforms
					postData.payload.browser = 'Native';
				}
				if (platform === 'android') {
					// Has to be exactly 'Android OS' since umami uses 'detect-browser' package
					// Check analytics/lib/constants.ts:180
					postData.payload.os = 'Android OS';
				}
				if (platform === 'ios') {
					// Has to be exactly 'iOS' since umami uses 'detect-browser' package
					// Check analytics/lib/constants.ts:180
					postData.payload.os = 'iOS';
				}
				await axios.post<string>(`${analyticsUrl}/api/send`, postData, {
					headers: {
						'Content-Type': 'text/plain',
					},
				});
			} catch (err) {
				console.debug(`Error sending ${eventType} data`, err);
			}
		},
		[
			analyticsUrl,
			fingerprint,
			clientUrl,
			websiteId,
			debug,
			location,
			hasPermission,
		],
	);

	const trackEvent = useCallback(
		(name: string, extraData?: { [key: string]: string }) =>
			sendEvent({ name, extraData }),
		[sendEvent],
	);

	const trackPage = useCallback(
		(args?: TrackPageArgs) => sendEvent(args),
		[sendEvent],
	);

	return (
		<AnalyticsContext.Provider
			value={{
				trackPage,
				trackEvent,
			}}
		>
			{children}
		</AnalyticsContext.Provider>
	);
};

export default AnalyticsProvider;
